import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardCode,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChartColumn,
  Compass,
  EllipsisVertical,
  Eye,
  Globe,
  GripVertical,
  Loader2,
  Pencil,
  Images,
  ImageUp,
  Plus,
  Settings,
  Share2,
  Trash2,
  TriangleAlert,
  Undo2,
  UploadCloud,
  Waypoints,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ShareModal } from "../components/ShareModal";
import { TourSettingsForm } from "../components/TourSettingsForm";
import { TourStatsPanel } from "../components/TourStatsPanel";
import { Button, ButtonLink, DropMenu, Input, Modal } from "../components/ui";
import {
  ProgressBar,
  progressState,
  UploadPanel,
  type ProgressState,
  type RejectedFile,
  type UploadEntry,
} from "../components/UploadPanel";
import { apiError, useT } from "../i18n";
import { apiJson, uploadFile } from "../lib/api";
import { registerLeaveGuard } from "../lib/leave-guard";
import { usePageCrumbs } from "../lib/page-header";
import { findTour, useProjects } from "../lib/projects";
import { StatusBadge } from "../components/TourCard";
import type {
  CreateSceneResponse,
  SceneDto,
  SceneStatusDto,
  TourDetail,
  TourSceneStatus,
} from "../../shared/api-types";

/** Die drei Tabs der Tour-Seite — der Tab steht in der URL (?tab=…), damit er einen Link hat. */
const TABS = ["scenes", "settings", "stats"] as const;
type Tab = (typeof TABS)[number];
const TAB_ICON: Record<Tab, ReactNode> = {
  scenes: <Images className="size-4" />,
  settings: <Settings className="size-4" />,
  stats: <ChartColumn className="size-4" />,
};

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 200 * 1024 * 1024;
const POLL_MS = 1500;

/** IMG_2 vor IMG_10 — die Reihenfolge aus dem Datei-Dialog ist sonst Laune des Systems. */
const byFileName = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function sceneNameFromFile(file: File): string {
  return (
    file.name
      .replace(/\.[^.]+$/, "")
      .replace(/[-_]+/g, " ")
      .trim() || "Szene"
  );
}

function IconButton({
  title,
  onClick,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`rounded-md p-1.5 text-zinc-400 transition-colors ${
        danger
          ? "hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          : "hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

// Karteninhalt separat, weil er zweimal gebraucht wird: im sortierbaren
// Grid-Element und als schwebender Klon im DragOverlay.
function SceneCardBody({
  scene,
  index,
  progress,
  localPreview,
  linkable,
  onRename,
  onReplace,
  onRestore,
  onDelete,
}: {
  scene: SceneDto;
  index: number;
  /** Wo die Szene gerade steht (Upload/Warteschlange/Verarbeitung/Fehler) */
  progress: ProgressState;
  /** Lokale Objekt-URL der hochgeladenen Datei — überbrückt, bis der Render da ist */
  localPreview: string | undefined;
  /** Tour hat mehr als eine Szene — nur dann sind Hotspot-Chips sinnvoll */
  linkable: boolean;
  onRename: () => void;
  onReplace: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const hotspotCount = scene.hotspots.length;
  const busy = progress.tone !== "done";
  // Solange die Szene nicht fertig ist, gewinnt das lokale Bild: beim Ersetzen steht in
  // previewUrl noch der alte Render — zu sehen sein soll aber das gerade gewählte Bild.
  const imageUrl =
    (scene.status === "ready" ? scene.previewUrl : null) ?? localPreview ?? scene.previewUrl;
  return (
    <>
      <div className="relative aspect-[2/1] overflow-hidden bg-zinc-100 dark:bg-zinc-800">
        {imageUrl && (
          <img
            src={imageUrl}
            alt={scene.name}
            // Schwarzweiß, solange das Bild noch nicht angekommen ist — mit dem
            // fertigen Upload kommt die Farbe, mit dem Render die Schärfe des Servers.
            // Bewusst die Standard-`transition` (nicht transition-[transform,…]):
            // Tailwind v4 setzt scale-* als eigene CSS-Eigenschaft `scale`, die
            // von einer reinen transform-Transition nicht erfasst würde — der Hover
            // spränge dann ohne Animation.
            className={`h-full w-full object-cover transition duration-300 sm:group-hover:scale-[1.04] ${
              progress.tone === "upload" ? "grayscale opacity-70" : busy ? "opacity-80" : ""
            }`}
            loading="lazy"
            draggable={false}
          />
        )}
        <span className="absolute left-2 top-2 rounded-md bg-black/50 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white backdrop-blur-sm">
          {index + 1}
        </span>
        <span
          title={t.tour.dragToSort}
          onClick={(e) => e.stopPropagation()}
          className="absolute right-2 top-2 hidden cursor-grab rounded-md bg-black/50 p-1 text-white opacity-0 backdrop-blur-sm transition-opacity active:cursor-grabbing group-hover:opacity-100 sm:block"
        >
          <GripVertical className="size-3.5" />
        </span>
        {scene.status === "ready" && linkable && (
          <span
            title={t.tour.hotspotCount(hotspotCount)}
            className={`absolute bottom-2 left-2 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-white ${
              hotspotCount > 0 ? "bg-black/50 backdrop-blur-sm" : "bg-amber-500/95"
            }`}
          >
            {hotspotCount > 0 ? (
              <>
                <Waypoints className="size-3" />
                {hotspotCount}
              </>
            ) : (
              <>
                <TriangleAlert className="size-3" />
                {t.tour.hotspotNone}
              </>
            )}
          </span>
        )}
        {scene.status === "ready" && (
          <span className="pointer-events-none absolute inset-0 hidden place-items-center opacity-0 transition-opacity duration-200 group-hover:opacity-100 sm:grid">
            <span className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
              <Compass className="size-3.5" />
              {t.tour.openScene}
            </span>
          </span>
        )}
        {/* Zustand am unteren Kartenrand statt Vollflächen-Overlay: das Bild bleibt
            sichtbar, und 20 Karten nebeneinander bleiben überschaubar. */}
        {busy && (
          <div
            className={`absolute inset-x-0 bottom-0 bg-gradient-to-t to-transparent px-2.5 pb-2 pt-7 ${
              progress.tone === "error"
                ? "from-red-800/90 via-red-800/60"
                : "from-black/75 via-black/50"
            }`}
          >
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-white">
              {progress.tone === "error" ? (
                <TriangleAlert className="size-3 shrink-0" />
              ) : (
                <Loader2 className="size-3 shrink-0 animate-spin" />
              )}
              {/* Fehlermeldungen sind lang — lieber zwei Zeilen als harte Kürzung */}
              <span
                className={`min-w-0 ${progress.tone === "error" ? "line-clamp-2" : "truncate"}`}
                title={progress.label}
              >
                {progress.label}
              </span>
            </div>
            {progress.tone !== "error" && (
              <ProgressBar
                percent={progress.percent}
                tone={progress.tone}
                thin
                className="bg-white/25"
              />
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-0.5 px-3 py-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{scene.name}</span>
        <span
          className="flex opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <IconButton title={t.tour.renameScene} onClick={onRename}>
            <Pencil className="size-4" />
          </IconButton>
          <IconButton title={t.tour.replaceScene} onClick={onReplace}>
            <ImageUp className="size-4" />
          </IconButton>
          {/* Nur nach einem misslungenen Tausch: das vorherige Bild liegt noch da */}
          {scene.status === "error" && scene.canRestoreImage && (
            <IconButton title={t.tour.restoreImage} onClick={onRestore}>
              <Undo2 className="size-4" />
            </IconButton>
          )}
          <IconButton title={t.tour.deleteScene} danger onClick={onDelete}>
            <X className="size-4" />
          </IconButton>
        </span>
      </div>
    </>
  );
}

const cardClass =
  "group relative overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900";

function SortableSceneCard({
  scene,
  onOpen,
  ...body
}: {
  scene: SceneDto;
  index: number;
  progress: ProgressState;
  localPreview: string | undefined;
  linkable: boolean;
  onOpen: () => void;
  onRename: () => void;
  onReplace: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: scene.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition: transition ?? undefined }}
      {...attributes}
      {...listeners}
      onKeyDown={(e) => {
        listeners?.onKeyDown?.(e);
        // Enter öffnet den Editor — Space hebt die Karte zum Sortieren an
        if (!e.defaultPrevented && e.key === "Enter" && !isDragging) onOpen();
      }}
      onClick={onOpen}
      className={`${cardClass} touch-manipulation select-none transition-[border-color,box-shadow] hover:border-zinc-300 hover:shadow-md dark:hover:border-zinc-600 ${
        scene.status === "ready" ? "cursor-pointer" : "cursor-default"
      } ${isDragging ? "opacity-40" : ""}`}
    >
      <SceneCardBody scene={scene} {...body} />
    </div>
  );
}

/** Antworten, nach denen es auf dieser Seite nichts mehr zu tun gibt. */
const GONE = new Set([
  "tour_not_found",
  "scene_not_found",
  "unauthorized",
  "forbidden",
  "no_account",
]);

export function TourPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState<TourDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Laufende Charge: Karten entstehen sofort für ALLE Dateien, hochgeladen wird
  // danach im Hintergrund. `batch` hält die Reihenfolge der aktuellen Runde,
  // `uploads` den Upload-Zustand je Szene, `sceneStatus` den Poll-Stand.
  const [uploads, setUploads] = useState<Record<string, UploadEntry>>({});
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const [batch, setBatch] = useState<string[]>([]);
  const [sceneStatus, setSceneStatus] = useState<Record<string, SceneStatusDto>>({});
  const objectUrls = useRef<string[]>([]);
  const batchSeeded = useRef(false);
  // EINE Warteschlange für alle Drops, EINE Schleife, die sie abarbeitet — sonst
  // startet jeder weitere Drop während des Uploads eine zweite Schleife, und zwei
  // XHRs wechseln sich ab statt nacheinander zu laufen. „Trotzdem verlassen“ bricht
  // über den Controller alles Laufende ab und leert die Schlange.
  const uploadQueue = useRef<{ sceneId: string; uploadUrl: string; file: File }[]>([]);
  const draining = useRef(false);
  const uploadAbort = useRef<AbortController | null>(null);
  // Szenen-Anlage ebenfalls serialisieren: der Server vergibt sortOrder als „max + 1“,
  // zwei gleichzeitige POSTs bekämen dieselbe Nummer.
  const intake = useRef(Promise.resolve());
  // Ganze Seite ist Dropzone: true, solange Dateien übers Fenster gezogen werden
  const [dropActive, setDropActive] = useState(false);
  const [dragSceneId, setDragSceneId] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  // Nach einem Drag darf der anschließende Browser-Click nicht den Editor öffnen
  const suppressClickUntil = useRef(0);
  const reorderInFlight = useRef(false);

  // Dialoge (bewusst keine Browser-Prompts)
  const [renameScene, setRenameScene] = useState<SceneDto | null>(null);
  const [replaceScene, setReplaceScene] = useState<SceneDto | null>(null);
  const [deleteScene, setDeleteScene] = useState<SceneDto | null>(null);
  const [deleteTourOpen, setDeleteTourOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const t = useT();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : "scenes";
  function selectTab(next: Tab) {
    const params = new URLSearchParams(searchParams);
    if (next === "scenes") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params);
  }

  // ?share=1 (Sprung aus dem Rundgang-Editor): direkt den Veröffentlichen-Dialog öffnen
  useEffect(() => {
    if (searchParams.get("share") !== "1") return;
    setShareOpen(true);
    const params = new URLSearchParams(searchParams);
    params.delete("share");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Touch: langes Drücken sortiert, kurzes Wischen scrollt weiter die Seite
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: {
        start: [KeyboardCode.Space],
        cancel: [KeyboardCode.Esc],
        end: [KeyboardCode.Space, KeyboardCode.Enter],
      },
    }),
  );

  const load = useCallback(async () => {
    try {
      setTour(await apiJson<TourDetail>(`/api/tours/${id}`));
    } catch (e) {
      const code = (e as Error).message;
      setError(apiError(t, code));
      // Tour gelöscht oder kein Zugriff mehr (z. B. Account in einem anderen Tab
      // gewechselt): laufende Uploads würden nur noch „nicht gefunden“ ernten, und
      // das Polling käme nie zum Ende — also alles verwerfen, es bleibt die Meldung.
      if (GONE.has(code)) {
        uploadQueue.current = [];
        uploadAbort.current?.abort();
        setTour(null);
        setUploads({});
        setBatch([]);
        setSceneStatus({});
      }
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Der Pfad steht in der Kopfzeile: Projekt › Tour
  usePageCrumbs(
    tour
      ? [{ label: tour.project.name, to: `/projects/${tour.project.id}` }, { label: tour.name }]
      : [],
  );

  // Seitenleiste und Übersicht ziehen nach, sobald diese Seite etwas zeigt, was
  // dort anders steht (Name, Status, Szenenzahl). Der Merker verhindert eine
  // Schleife, falls beide Seiten sich dauerhaft uneins wären.
  const { projects, reload: reloadProjects } = useProjects();
  const reloadedFor = useRef("");
  useEffect(() => {
    if (!tour || !projects) return;
    const listed = findTour(projects, tour.id)?.tour;
    const stale =
      !listed ||
      listed.name !== tour.name ||
      listed.status !== tour.status ||
      listed.hasChanges !== tour.hasChanges ||
      listed.sceneCount !== tour.scenes.length;
    const signature = `${tour.name}|${tour.status}|${tour.hasChanges}|${tour.scenes.length}`;
    if (!stale || reloadedFor.current === signature) return;
    reloadedFor.current = signature;
    reloadProjects();
  }, [tour, projects, reloadProjects]);

  const uploadActive = Object.values(uploads).some(
    (u) => u.state === "waiting" || u.state === "uploading",
  );
  // Sicherheitsnetz: ein fertiger Upload, dessen Szene lokal noch nicht final ist,
  // hält das Polling ebenfalls am Leben — egal, was der Rest der Tour gerade tut.
  const settling = Object.values(uploads).some((u) => {
    if (u.state !== "done") return false;
    const status = tour?.scenes.find((s) => s.id === u.sceneId)?.status;
    return status !== undefined && status !== "ready" && status !== "error";
  });
  const busy =
    uploadActive || settling || (tour?.scenes.some((s) => s.status === "processing") ?? false);

  // Solange etwas läuft, wird nur der schlanke Szenen-Status gepollt. Die komplette
  // Tour (Hotspots, Render-URLs) wird erst nachgeladen, wenn sich ein Status wirklich
  // geändert hat — das spart Last und stört das Sortieren nicht.
  const scenesRef = useRef<SceneDto[]>([]);
  scenesRef.current = tour?.scenes ?? [];
  useEffect(() => {
    if (!busy) return;
    let alive = true;
    const poll = async () => {
      try {
        const next = await apiJson<TourSceneStatus>(`/api/tours/${id}/scene-status`);
        if (!alive) return;
        setSceneStatus(Object.fromEntries(next.scenes.map((s) => [s.id, s])));
        const known = new Map(scenesRef.current.map((s) => [s.id, s.status]));
        const changed =
          next.scenes.length !== known.size ||
          next.scenes.some((s) => known.get(s.id) !== s.status);
        // Nicht mitten in einer Umsortierung laden — sonst springt die alte Reihenfolge zurück
        if (changed && !reorderInFlight.current) await load();
      } catch (e) {
        // Ein verpasster Poll ist folgenlos — außer die Tour ist weg: dann würde die
        // Anzeige ewig „verarbeitet“ zeigen, weil nie ein neuer Stand kommt.
        if (GONE.has((e as Error).message)) await load();
      }
    };
    const timer = setInterval(() => void poll(), POLL_MS);
    void poll();
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [busy, id, load]);

  // Seite mitten in der Verarbeitung geöffnet (Reload, zweites Gerät)? Dann ist die
  // laufende Charge das, was der Server noch verarbeitet — einmalig übernehmen.
  useEffect(() => {
    if (!tour || batchSeeded.current) return;
    batchSeeded.current = true;
    const running = tour.scenes.filter((s) => s.status === "processing").map((s) => s.id);
    if (running.length > 0) setBatch(running);
  }, [tour]);

  /** Charge abräumen: Anzeige weg, lokale Vorschaubilder freigeben. */
  const clearBatch = useCallback(() => {
    setUploads((current) => {
      for (const entry of Object.values(current)) URL.revokeObjectURL(entry.preview);
      objectUrls.current = [];
      return {};
    });
    setBatch([]);
    setRejected([]);
  }, []);

  // Alles durch und nichts schiefgegangen → Leiste nach kurzem Nachklang selbst
  // abräumen. Mit Fehlern bleibt sie stehen, bis sie jemand wegklickt.
  const batchDone =
    batch.length > 0 &&
    !uploadActive &&
    batch.every((sid) => {
      const status = sceneStatus[sid]?.status ?? tour?.scenes.find((s) => s.id === sid)?.status;
      return status === undefined || status === "ready" || status === "error";
    });
  const batchFailed =
    rejected.length > 0 ||
    Object.values(uploads).some((u) => u.state === "error") ||
    batch.some(
      (sid) =>
        (sceneStatus[sid]?.status ?? tour?.scenes.find((s) => s.id === sid)?.status) === "error",
    );
  useEffect(() => {
    if (!batchDone || batchFailed) return;
    const timer = setTimeout(clearBatch, 6000);
    return () => clearTimeout(timer);
  }, [batchDone, batchFailed, clearBatch]);

  // Uploads sterben mit der Seite — das darf niemandem versehentlich passieren.
  // Tab schließen/Reload/location.assign: Browser-Dialog (die einzige Möglichkeit dort).
  useEffect(() => {
    if (!uploadActive) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [uploadActive]);

  // In-App-Navigation (Logo, Szene öffnen, Abmelden …): eigener Dialog. Eine
  // SPA-Navigation würde die XHRs nämlich NICHT abbrechen — sie liefen unsichtbar
  // weiter, und die Karten zeigten beim Zurückkommen „fehlgeschlagen“ für Bilder,
  // die gerade ankommen. Deshalb hier anhalten und beim Verlassen wirklich abbrechen.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      uploadActive && currentLocation.pathname !== nextLocation.pathname,
  );
  // abandonUploads() läuft über ein await hinweg — danach ist `blocker` aus der
  // Closure veraltet (proceed() auf einem schon freigegebenen Blocker wirft).
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;
  const leaving = useRef(false);
  useEffect(() => {
    // Uploads sind von selbst fertig geworden, während der Dialog offen war → weiter
    if (!leaving.current && blocker.state === "blocked" && !uploadActive) blocker.proceed();
  }, [blocker, uploadActive]);

  // Account wechseln/anlegen (Kopfzeile) läuft nicht über den Router, stellt aber die
  // Sitzung serverseitig um — fragt deshalb über die Verlassen-Sperre vorher nach und
  // bekommt hier denselben Dialog. resolve(true) erst, wenn die Uploads abgebrochen sind.
  const [pendingLeave, setPendingLeave] = useState<{ resolve: (ok: boolean) => void } | null>(null);
  useEffect(() => {
    if (!uploadActive) return;
    registerLeaveGuard(() => new Promise((resolve) => setPendingLeave({ resolve })));
    return () => registerLeaveGuard(null);
  }, [uploadActive]);

  function stay() {
    blockerRef.current.reset?.();
    pendingLeave?.resolve(false);
    setPendingLeave(null);
  }

  /** „Trotzdem verlassen“: XHRs abbrechen, Szenen ohne Bild entfernen, dann navigieren. */
  async function abandonUploads() {
    leaving.current = true;
    uploadQueue.current = [];
    uploadAbort.current?.abort();
    // Nur die Szenen, die es ohne diesen Upload gar nicht gäbe. Ein abgebrochener
    // Bildtausch lässt die Szene unverändert stehen — der Server hat sie nie angefasst.
    const orphaned = Object.values(uploads).filter((u) => u.state !== "done" && !u.replacing);
    const ids = new Set(orphaned.map((u) => u.sceneId));
    setUploads((u) => Object.fromEntries(Object.entries(u).filter(([id]) => !ids.has(id))));
    setBatch((b) => b.filter((id) => !ids.has(id)));
    setTour((current) =>
      current ? { ...current, scenes: current.scenes.filter((sc) => !ids.has(sc.id)) } : current,
    );
    await Promise.allSettled(
      orphaned.map((u) => apiJson(`/api/scenes/${u.sceneId}`, { method: "DELETE" })),
    );
    leaving.current = false;
    const current = blockerRef.current;
    if (current.state === "blocked") current.proceed();
    pendingLeave?.resolve(true);
    setPendingLeave(null);
  }

  useEffect(() => {
    const urls = objectUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  /** Eine Datei hochladen — Karte und Sammelleiste lesen den Zustand aus `uploads`. */
  const uploadOne = useCallback(
    async (sceneId: string, uploadUrl: string, file: File, signal: AbortSignal) => {
      const patch = (change: Partial<UploadEntry>) =>
        setUploads((u) => (u[sceneId] ? { ...u, [sceneId]: { ...u[sceneId], ...change } } : u));
      patch({ state: "uploading", loaded: 0, error: undefined });
      try {
        await uploadFile(uploadUrl, file, (loaded) => patch({ loaded }), signal);
        patch({ state: "done", loaded: file.size });
        // Der Server hat die Szene mit dem PUT auf "processing" gesetzt — lokal
        // nachziehen. Sonst kippt `busy` beim letzten Upload auf false (lokal steht
        // die Szene noch auf "uploading"), das Polling endet, und die Karte bliebe
        // für immer auf „Wird verarbeitet“ stehen. Gilt genauso fürs Ersetzen, wo die
        // Szene lokal noch auf "ready" steht.
        setTour((current) =>
          current
            ? {
                ...current,
                scenes: current.scenes.map((sc) =>
                  sc.id === sceneId
                    ? { ...sc, status: "processing", progress: 0, phase: null }
                    : sc,
                ),
              }
            : current,
        );
      } catch (e) {
        // Abbruch ist kein Fehler — abandonUploads() räumt den Eintrag selbst weg
        if ((e as Error).message === "aborted") return;
        const code = (e as Error).message;
        patch({ state: "error", error: apiError(t, code) });
        // „Nicht gefunden“ heißt: Szene, Tour oder Zugriff sind weg — nachladen klärt,
        // ob nur diese Szene fehlt oder die ganze Seite hinfällig ist (dann räumt
        // load() auf und die Schlange läuft leer, statt 40-mal denselben Fehler zu holen).
        if (GONE.has(code)) await load();
      }
    },
    [t, load],
  );

  /** Arbeitet die Warteschlange ab — läuft höchstens einmal, egal wie oft sie angestoßen wird. */
  const drain = useCallback(async () => {
    if (draining.current) return;
    draining.current = true;
    try {
      if (!uploadAbort.current || uploadAbort.current.signal.aborted) {
        uploadAbort.current = new AbortController();
      }
      const { signal } = uploadAbort.current;
      // Nacheinander, bewusst NICHT parallel: die Leitung ist der Engpass, parallele
      // Uploads teilen sie sich nur — das erste Bild käme entsprechend später an, und
      // der Worker säße solange untätig. Seriell überlappen Upload und Verarbeitung
      // ab der ersten Datei.
      for (let item = uploadQueue.current.shift(); item; item = uploadQueue.current.shift()) {
        if (signal.aborted) {
          uploadQueue.current = [];
          break;
        }
        await uploadOne(item.sceneId, item.uploadUrl, item.file, signal);
      }
    } finally {
      draining.current = false;
    }
  }, [uploadOne]);

  /**
   * Bild einer bestehenden Szene tauschen: kein neuer Datensatz, nur ein zweiter
   * Upload auf dieselbe Szene. Damit bleiben Hotspots (auch die Links anderer Szenen
   * hierher), Name, Reihenfolge und Startansicht, wo sie sind. Fortschritt und
   * Verarbeitung laufen über dieselbe Warteschlange wie ein normaler Upload.
   */
  function startReplace(scene: SceneDto, file: File) {
    setReplaceScene(null);
    const key = `${file.name}-${file.size}-${file.lastModified}`;
    if (!ACCEPTED.includes(file.type)) {
      setRejected((r) => [...r, { key, name: file.name, error: t.apiError.unsupportedMediaType }]);
      return;
    }
    if (file.size > MAX_BYTES) {
      setRejected((r) => [...r, { key, name: file.name, error: t.apiError.fileTooLarge }]);
      return;
    }
    const preview = URL.createObjectURL(file);
    objectUrls.current.push(preview);
    // Ersetzen nach einem Fehlschlag: sonst blitzt zwischen fertigem Upload und
    // erstem Poll noch einmal die alte Fehlermeldung auf.
    forgetSceneStatus(scene.id);
    const uploadUrl = `/api/scenes/${scene.id}/original`;
    setUploads((u) => ({
      ...u,
      [scene.id]: {
        sceneId: scene.id,
        file,
        uploadUrl,
        name: file.name,
        bytes: file.size,
        loaded: 0,
        preview,
        state: "waiting",
        replacing: true,
      },
    }));
    setBatch((b) => (b.includes(scene.id) ? b : [...b, scene.id]));
    // Optimistisch, inkl. Badge — der Server vermerkt den Tausch schon beim Upload
    setTour((current) =>
      current ? { ...current, hasChanges: current.status === "published" } : current,
    );
    uploadQueue.current.push({ sceneId: scene.id, uploadUrl, file });
    void drain();
  }

  /**
   * Den zuletzt gepollten Stand einer Szene vergessen. Nötig, wenn sich der Zustand
   * ohne Polling ändert: mit einem Fehler endet das Polling, und der letzte Stand sagt
   * dann für immer „Fehler“ — er hat in progressState Vorrang vor der Szene selbst.
   */
  function forgetSceneStatus(sceneId: string) {
    setSceneStatus((current) => {
      if (!current[sceneId]) return current;
      const rest = { ...current };
      delete rest[sceneId];
      return rest;
    });
  }

  /**
   * Misslungener Tausch: zurück auf das Bild, das noch im Render steckt. Ohne
   * Rückfrage — es stellt genau den Stand her, der vor dem Tausch da war.
   */
  async function restoreImage(scene: SceneDto) {
    setUploads((u) => {
      const rest = { ...u };
      delete rest[scene.id];
      return rest;
    });
    setBatch((b) => b.filter((id) => id !== scene.id));
    forgetSceneStatus(scene.id);
    try {
      await apiJson(`/api/scenes/${scene.id}/restore-image`, { method: "POST" });
    } catch (e) {
      setError(apiError(t, (e as Error).message));
    }
    await load();
  }

  function handleFiles(fileList: Iterable<File>) {
    const files = [...fileList];
    // Anlage hinter die vorherige hängen (nicht die Uploads — die laufen nebenher)
    intake.current = intake.current.then(() => intakeFiles(files));
  }

  async function intakeFiles(fileList: File[]) {
    setError(null);
    // Reihenfolge im Datei-Dialog ist Laune des Systems — nach Namen sortieren,
    // damit die Szenen-Nummern zu den Bilddateien passen.
    const files = [...fileList].sort((a, b) => byFileName.compare(a.name, b.name));
    const accepted: File[] = [];
    const bad: RejectedFile[] = [];
    for (const file of files) {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      if (!ACCEPTED.includes(file.type)) {
        bad.push({ key, name: file.name, error: t.apiError.unsupportedMediaType });
      } else if (file.size > MAX_BYTES) {
        bad.push({ key, name: file.name, error: t.apiError.fileTooLarge });
      } else {
        accepted.push(file);
      }
    }
    if (bad.length > 0) setRejected((r) => [...r, ...bad]);
    if (accepted.length === 0) return;

    // Schritt 1: alle Szenen anlegen. Dadurch stehen sofort alle Karten da, statt
    // eine nach der anderen aufzutauchen. Nacheinander, weil sortOrder aus dem
    // aktuellen Maximum abgeleitet wird — parallel griffen zwei nach demselben Platz.
    const queued: { sceneId: string; uploadUrl: string; file: File }[] = [];
    for (const file of accepted) {
      try {
        const { scene, uploadUrl } = await apiJson<CreateSceneResponse>(`/api/tours/${id}/scenes`, {
          method: "POST",
          json: { name: sceneNameFromFile(file), contentType: file.type },
        });
        const preview = URL.createObjectURL(file);
        objectUrls.current.push(preview);
        setTour((current) =>
          current ? { ...current, scenes: [...current.scenes, scene] } : current,
        );
        setUploads((u) => ({
          ...u,
          [scene.id]: {
            sceneId: scene.id,
            file,
            uploadUrl,
            name: file.name,
            bytes: file.size,
            loaded: 0,
            preview,
            state: "waiting",
          },
        }));
        setBatch((b) => [...b, scene.id]);
        queued.push({ sceneId: scene.id, uploadUrl, file });
      } catch (e) {
        setRejected((r) => [
          ...r,
          {
            key: `${file.name}-${file.lastModified}-create`,
            name: file.name,
            error: apiError(t, (e as Error).message),
          },
        ]);
      }
    }

    // Schritt 2: hinten an die gemeinsame Schlange — läuft schon eine, reiht sich
    // dieser Drop einfach dahinter ein.
    uploadQueue.current.push(...queued);
    void drain();
  }

  // handleFiles hängt an t/id — für die einmalig registrierten Window-Listener
  // immer die aktuelle Fassung über eine Ref greifbar machen
  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  // Ablegen nur im Szenen-Tab: in Einstellungen oder Statistik gäbe es keine Karte,
  // auf der die neue Szene auftauchen könnte.
  useEffect(() => {
    if (tab !== "scenes") return;
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files") ?? false;
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDropActive(true);
    };
    const onOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      // Ohne preventDefault öffnet der Browser die Datei im Tab
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      // dragleave feuert bei jedem Kind-Element — Zähler statt Flackern
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDropActive(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDropActive(false);
      if (e.dataTransfer?.files.length) void handleFilesRef.current(e.dataTransfer.files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
      // Tab-Wechsel mitten im Ziehen: sonst bliebe das Overlay stehen
      dragDepth.current = 0;
      setDropActive(false);
    };
  }, [tab]);

  function onSortStart(e: DragStartEvent) {
    setDragSceneId(String(e.active.id));
  }

  function onSortEnd(e: DragEndEvent) {
    setDragSceneId(null);
    suppressClickUntil.current = Date.now() + 300;
    const { active, over } = e;
    if (!tour || !over || active.id === over.id) return;
    const from = tour.scenes.findIndex((s) => s.id === active.id);
    const to = tour.scenes.findIndex((s) => s.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(tour.scenes, from, to);
    // Optimistisch, inkl. Badge — die Reihenfolge zählt zum veröffentlichbaren Inhalt
    setTour({ ...tour, scenes: next, hasChanges: tour.status === "published" });
    reorderInFlight.current = true;
    void apiJson(`/api/tours/${id}/scenes/order`, {
      method: "POST",
      json: { sceneIds: next.map((s) => s.id) },
    })
      .catch(async (err) => {
        setError(apiError(t, (err as Error).message));
        await load();
      })
      .finally(() => {
        reorderInFlight.current = false;
      });
  }

  function openScene(scene: SceneDto) {
    if (Date.now() < suppressClickUntil.current) return;
    if (scene.status !== "ready") return;
    navigate(`/tours/${id}/editor?scene=${scene.id}`);
  }

  async function submitRenameScene(e: FormEvent) {
    e.preventDefault();
    if (!renameScene) return;
    await apiJson(`/api/scenes/${renameScene.id}`, { method: "PATCH", json: { name: nameValue } });
    setRenameScene(null);
    await load();
  }

  // Fehlerhafte Uploads: Verarbeitung gescheitert (z. B. kein 2:1-Bild) oder ein
  // Upload, der ohne Datei hängen geblieben ist (frühere Sitzung). Laufende oder
  // fertige Uploads, deren Status nur noch nicht nachgeladen ist, zählen nicht.
  const failedScenes = (tour?.scenes ?? []).filter((s) => {
    // Eine Szene mit Render war schon einmal fertig: ein gescheiterter Bildtausch macht
    // sie nicht zum Müll. Löschen würde ihre Hotspots mitnehmen — der Weg heraus ist
    // ein anderes Bild (Karte → Bild ersetzen), nicht der Sammel-Aufräumer.
    if (s.render) return false;
    if (s.status === "error") return true;
    const entry = uploads[s.id];
    return s.status === "uploading" && (!entry || entry.state === "error");
  });
  const [removingFailed, setRemovingFailed] = useState(false);

  /** Alle fehlerhaften Szenen auf einmal weg — statt zehnmal Karte → Löschen → Bestätigen. */
  async function removeFailed() {
    const ids = new Set(failedScenes.map((s) => s.id));
    setRemovingFailed(true);
    await Promise.allSettled(
      [...ids].map((id) => apiJson(`/api/scenes/${id}`, { method: "DELETE" })),
    );
    setBatch((b) => b.filter((id) => !ids.has(id)));
    setUploads((u) => {
      const rest = { ...u };
      for (const id of ids) {
        if (rest[id]) URL.revokeObjectURL(rest[id].preview);
        delete rest[id];
      }
      return rest;
    });
    await load();
    setRemovingFailed(false);
  }

  async function confirmDeleteScene() {
    if (!deleteScene) return;
    const sceneId = deleteScene.id;
    await apiJson(`/api/scenes/${sceneId}`, { method: "DELETE" });
    setDeleteScene(null);
    // Auch aus der laufenden Charge nehmen, sonst zählt die Leiste eine Szene mit,
    // die es nicht mehr gibt.
    setBatch((b) => b.filter((sid) => sid !== sceneId));
    setUploads((u) => {
      const rest = { ...u };
      if (rest[sceneId]) URL.revokeObjectURL(rest[sceneId].preview);
      delete rest[sceneId];
      return rest;
    });
    await load();
  }

  async function confirmDeleteTour() {
    await apiJson(`/api/tours/${id}`, { method: "DELETE" });
    reloadProjects();
    navigate("/");
  }

  async function confirmUnpublish() {
    await apiJson(`/api/tours/${id}/unpublish`, { method: "POST" });
    setUnpublishOpen(false);
    await load();
  }

  if (!tour) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-8">
        {error ? (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : (
          <p className="text-sm text-zinc-400">{t.common.loading}</p>
        )}
      </main>
    );
  }

  const linkable = tour.scenes.length > 1;
  const hasReady = tour.scenes.some((s) => s.status === "ready");
  const dragScene = tour.scenes.find((s) => s.id === dragSceneId) ?? null;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <h2 className="truncate text-lg font-semibold">{tour.name}</h2>
            <StatusBadge
              status={tour.status}
              hasChanges={tour.hasChanges}
              locked={tour.passwordProtected}
            />
          </div>
        </div>
        <div className="flex w-full gap-2 sm:ml-auto sm:w-auto">
          {/* Veröffentlicht wird im Dialog — hier nur der Einstieg, beschriftet nach Lage */}
          <Button className="flex-1 sm:flex-none" onClick={() => setShareOpen(true)}>
            {tour.status === "published" ? (
              <Share2 className="size-3.5" />
            ) : (
              <Globe className="size-3.5" />
            )}
            {tour.status !== "published"
              ? t.share.publish
              : tour.hasChanges
                ? t.share.republish
                : t.share.share}
          </Button>
          <Button
            variant="ghost"
            disabled={!hasReady}
            title={t.editor.editTour}
            onClick={() => navigate(`/tours/${id}/editor`)}
          >
            <Compass className="size-3.5" />
            <span className="hidden sm:inline">{t.editor.editTour}</span>
          </Button>
          <ButtonLink
            variant="ghost"
            square
            href={`/preview/${id}`}
            target="_blank"
            rel="noreferrer"
            title={`${t.tour.preview} — ${t.tour.previewHint}`}
            className={hasReady ? "" : "pointer-events-none opacity-50"}
          >
            <Eye className="size-4" />
          </ButtonLink>
          <DropMenu
            button={<EllipsisVertical className="size-4" />}
            title={t.tour.moreActions}
            items={[
              ...(tour.status === "published"
                ? [
                    {
                      label: t.share.unpublish,
                      icon: <Undo2 className="size-4" />,
                      onClick: () => setUnpublishOpen(true),
                    },
                  ]
                : []),
              "divider" as const,
              {
                label: t.tour.delete,
                icon: <Trash2 className="size-4" />,
                danger: true,
                onClick: () => setDeleteTourOpen(true),
              },
            ]}
          />
        </div>
      </div>

      {/* Szenen · Einstellungen · Statistik — eine Seite, drei Tabs */}
      <div className="-mt-2 mb-6 border-b border-zinc-200 dark:border-zinc-800">
        <nav className="-mb-px flex gap-6 text-sm">
          {TABS.map((key) => (
            <button
              key={key}
              onClick={() => selectTab(key)}
              aria-current={tab === key ? "page" : undefined}
              className={`flex items-center gap-1.5 border-b-2 pb-2.5 transition-colors ${
                tab === key
                  ? "border-accent font-medium text-zinc-900 dark:text-zinc-50"
                  : "border-transparent text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
              }`}
            >
              {TAB_ICON[key]}
              {key === "scenes"
                ? t.tour.tabScenes
                : key === "settings"
                  ? t.tour.settings
                  : t.stats.title}
            </button>
          ))}
        </nav>
      </div>

      {tab === "scenes" && (
        <>
          {/* Datei-Dialog — die ganze Seite ist Dropzone (Window-Listener oben) */}
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED.join(",")}
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = "";
            }}
          />

          {/* Leerzustand: sichtbare Klick-/Drop-Fläche als einziger Einstieg */}
          {tour.scenes.length === 0 && (
            <div
              onClick={() => fileInput.current?.click()}
              className="mb-4 cursor-pointer rounded-2xl border-2 border-dashed border-zinc-200 p-16 text-center transition-colors hover:border-accent/60 dark:border-zinc-700 dark:hover:border-accent/60"
            >
              <UploadCloud className="mx-auto mb-2 size-7 text-zinc-300 dark:text-zinc-600" />
              <p className="font-medium">{t.tour.dropTitle}</p>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.tour.dropText}</p>
            </div>
          )}

          {/* Fehlerhafte Uploads gesammelt — die roten Karten allein sagen nicht, wie viele es sind */}
          {failedScenes.length > 0 && (
            <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              <TriangleAlert className="size-5 shrink-0 text-red-500" />
              <span className="min-w-0 flex-1">{t.tour.failedBanner(failedScenes.length)}</span>
              <Button
                variant="danger"
                disabled={removingFailed}
                onClick={() => void removeFailed()}
                className="bg-white dark:bg-zinc-900"
              >
                <Trash2 className="size-3.5" />
                {t.tour.removeFailed(failedScenes.length)}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Drop-Overlay unterhalb der Nav, solange Dateien über dem Fenster schweben */}
      {dropActive && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 top-14 z-40 bg-white/60 p-4 backdrop-blur-[2px] dark:bg-zinc-950/60">
          <div className="grid h-full place-items-center rounded-2xl border-2 border-dashed border-accent bg-accent/5 text-center">
            <div>
              <UploadCloud className="mx-auto mb-3 size-8 text-accent" />
              <p className="text-lg font-semibold">{t.tour.dropOverlayTitle}</p>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{t.tour.dropText}</p>
            </div>
          </div>
        </div>
      )}

      {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* Eine Leiste für die ganze Charge — Upload oben, Verarbeitung darunter */}
      <UploadPanel
        batch={batch}
        uploads={uploads}
        rejected={rejected}
        scenes={tour.scenes}
        sceneStatus={sceneStatus}
        onDismiss={clearBatch}
      />

      {/* Szenen — im Leerzustand reicht die Dropzone, kein doppelter Hinweis */}
      {tab === "scenes" && tour.scenes.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onSortStart}
          onDragEnd={onSortEnd}
          onDragCancel={() => setDragSceneId(null)}
        >
          <SortableContext items={tour.scenes.map((s) => s.id)} strategy={rectSortingStrategy}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tour.scenes.map((scene, i) => (
                <SortableSceneCard
                  key={scene.id}
                  scene={scene}
                  index={i}
                  progress={progressState(t, scene, sceneStatus[scene.id], uploads[scene.id])}
                  localPreview={uploads[scene.id]?.preview}
                  linkable={linkable}
                  onOpen={() => openScene(scene)}
                  onRename={() => {
                    setNameValue(scene.name);
                    setRenameScene(scene);
                  }}
                  onReplace={() => setReplaceScene(scene)}
                  onRestore={() => void restoreImage(scene)}
                  onDelete={() => setDeleteScene(scene)}
                />
              ))}
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="grid min-h-40 place-items-center rounded-2xl border-2 border-dashed border-zinc-200 text-zinc-400 transition-colors hover:border-accent/60 hover:text-accent dark:border-zinc-700 dark:text-zinc-500 dark:hover:border-accent/60 dark:hover:text-accent"
              >
                <span className="flex flex-col items-center gap-1.5 text-sm font-medium">
                  <Plus className="size-6" />
                  {t.tour.addScenes}
                  <span className="text-xs font-normal text-zinc-400 dark:text-zinc-500">
                    {t.tour.orDrop}
                  </span>
                </span>
              </button>
            </div>
          </SortableContext>
          <DragOverlay>
            {dragScene && (
              <div className={`${cardClass} shadow-2xl dark:border-zinc-700`}>
                <SceneCardBody
                  scene={dragScene}
                  index={tour.scenes.findIndex((s) => s.id === dragScene.id)}
                  progress={progressState(
                    t,
                    dragScene,
                    sceneStatus[dragScene.id],
                    uploads[dragScene.id],
                  )}
                  localPreview={uploads[dragScene.id]?.preview}
                  linkable={linkable}
                  onRename={() => {}}
                  onReplace={() => {}}
                  onRestore={() => {}}
                  onDelete={() => {}}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {tab === "settings" && <TourSettingsForm tour={tour} onSaved={load} variant="full" />}

      {tab === "stats" && <TourStatsPanel tourId={id} />}

      {/* Dialoge */}
      <Modal
        open={renameScene !== null}
        onClose={() => setRenameScene(null)}
        title={t.tour.renameSceneTitle}
      >
        <form onSubmit={(e) => void submitRenameScene(e)} className="space-y-4">
          <Input
            className="w-full"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            required
            autoFocus
            selectOnFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setRenameScene(null)}>
              {t.common.cancel}
            </Button>
            <Button type="submit">{t.common.save}</Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={replaceScene !== null}
        onClose={() => setReplaceScene(null)}
        title={t.tour.replaceSceneTitle(replaceScene?.name ?? "")}
      >
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          {t.tour.replaceSceneText}
          {replaceScene && tour.publishedSceneIds.includes(replaceScene.id) && (
            <>
              <br />
              {t.tour.replaceScenePublished}
            </>
          )}
          <br />
          {t.tour.replaceSceneWarn}
        </p>
        {/* Datei-Dialog dieses Modals — die Auswahl startet den Tausch sofort */}
        <input
          ref={replaceInput}
          type="file"
          accept={ACCEPTED.join(",")}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file && replaceScene) startReplace(replaceScene, file);
          }}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setReplaceScene(null)}>
            {t.common.cancel}
          </Button>
          <Button onClick={() => replaceInput.current?.click()}>
            <ImageUp className="size-3.5" />
            {t.tour.replaceScenePick}
          </Button>
        </div>
      </Modal>

      <Modal
        open={deleteScene !== null}
        onClose={() => setDeleteScene(null)}
        title={t.tour.deleteSceneTitle(deleteScene?.name ?? "")}
      >
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          {t.tour.deleteSceneText}
          {deleteScene && tour.publishedSceneIds.includes(deleteScene.id) && (
            <>
              <br />
              {t.tour.deleteScenePublished}
            </>
          )}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDeleteScene(null)}>
            {t.common.cancel}
          </Button>
          <Button variant="danger" onClick={() => void confirmDeleteScene()}>
            {t.common.deleteForever}
          </Button>
        </div>
      </Modal>

      <Modal
        open={deleteTourOpen}
        onClose={() => setDeleteTourOpen(false)}
        title={t.tour.deleteTitle(tour.name)}
      >
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">{t.tour.deleteText}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDeleteTourOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button variant="danger" onClick={() => void confirmDeleteTour()}>
            {t.common.deleteForever}
          </Button>
        </div>
      </Modal>

      <Modal
        open={unpublishOpen}
        onClose={() => setUnpublishOpen(false)}
        title={t.share.unpublishTitle}
      >
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">{t.share.unpublishText}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setUnpublishOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button variant="danger" onClick={() => void confirmUnpublish()}>
            {t.share.unpublish}
          </Button>
        </div>
      </Modal>

      {/* Navigation angehalten, weil noch Uploads laufen */}
      <Modal
        open={blocker.state === "blocked" || pendingLeave !== null}
        onClose={stay}
        title={t.upload.leaveTitle}
      >
        <p className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          {t.upload.leaveText(
            Object.values(uploads).filter((u) => u.state !== "done").length,
            Object.values(uploads).filter((u) => u.state === "done").length,
          )}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={stay}>
            {t.upload.leaveStay}
          </Button>
          <Button variant="danger" onClick={() => void abandonUploads()}>
            {t.upload.leaveAnyway}
          </Button>
        </div>
      </Modal>

      <ShareModal
        tour={tour}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onChanged={load}
      />
    </main>
  );
}
